/**
 * Typed Socket.IO client for the `/game` namespace.
 *
 * Responsibilities, in order of importance:
 *  1. never let an unvalidated payload reach the store (see parse.ts);
 *  2. keep the Clock synchronised, because every displayed multiplier depends
 *     on it — and report when it is not, so betting can be disabled;
 *  3. turn ack callbacks into promises, so the UI awaits the SERVER decision
 *     instead of guessing a cashout locally.
 */
import { io, type Socket } from "socket.io-client";
import {
  GAME_NAMESPACE,
  type Ack,
  type ClientToServerEvents,
  type MyBet,
  type PlaceBetInput,
  type ServerToClientEvents,
} from "@crashx/shared";
import { Clock } from "../game/core/Clock.js";
import type { MultiplierModel } from "../game/core/MultiplierModel.js";
import { createPayloadParser } from "./parse.js";
import { SERVER_EVENT_NAMES, type ServerEventName, type ServerEventPayload } from "./eventSchemas.js";
import type { ConnectionStatus } from "../state/connectionStore.js";

/** How often the clock offset is re-measured while connected. */
const PING_INTERVAL_MS = 5_000;
/** An ack the server never answers: fail the action rather than hang the button. */
const ACK_TIMEOUT_MS = 8_000;

export type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export interface CashoutResult {
  betId: string;
  multiplier: number;
  win: number;
}

export interface GameConnectionHandlers {
  onStatus: (status: ConnectionStatus) => void;
  onClockHealth: (health: { latencyMs: number; synced: boolean }) => void;
  onEvent: <K extends ServerEventName>(event: K, payload: ServerEventPayload<K>) => void;
}

export interface GameConnectionOptions {
  /** Defaults to the current origin (Vite proxies /socket.io in dev). */
  url?: string;
  clock?: Clock;
  multiplier?: MultiplierModel;
  handlers: GameConnectionHandlers;
  /** Injected in tests. */
  createSocket?: (url: string | undefined) => GameSocket;
}

type AckEvent = "bet:place" | "bet:cancel" | "bet:cashout" | "client:seed";

interface LooseEmitter {
  emit: (event: string, payload: unknown, ack: (res: never) => void) => unknown;
}

export class GameConnection {
  readonly clock: Clock;
  private readonly socket: GameSocket;
  private readonly handlers: GameConnectionHandlers;
  private readonly multiplier: MultiplierModel | undefined;
  private readonly parse = createPayloadParser();
  private pingTimer: ReturnType<typeof setInterval> | null = null;

  constructor(options: GameConnectionOptions) {
    this.clock = options.clock ?? new Clock();
    this.handlers = options.handlers;
    this.multiplier = options.multiplier;
    this.socket = options.createSocket
      ? options.createSocket(options.url)
      : io((options.url ?? "") + GAME_NAMESPACE, {
          transports: ["websocket", "polling"],
          withCredentials: true,
          autoConnect: false,
        });
    this.wire();
  }

  connect(): void {
    this.handlers.onStatus("connecting");
    this.socket.connect();
  }

  disconnect(): void {
    this.stopPinging();
    this.socket.disconnect();
  }

  /** Place a bet. Resolves with the server ack — never with a local guess. */
  placeBet(input: Omit<PlaceBetInput, "clientRef">): Promise<Ack<MyBet>> {
    const payload: PlaceBetInput = { ...input, clientRef: newClientRef() };
    return this.emitWithAck<MyBet>("bet:place", payload);
  }

  cancelBet(betId: string): Promise<Ack<{ betId: string }>> {
    return this.emitWithAck<{ betId: string }>("bet:cancel", { betId });
  }

  /**
   * Cash out. `clientMultiplier` is informational: the server recomputes the
   * payout from its own clock, and its answer is the only truth.
   */
  cashout(betId: string, clientMultiplier?: number): Promise<Ack<CashoutResult>> {
    const payload = clientMultiplier === undefined ? { betId } : { betId, clientMultiplier };
    return this.emitWithAck<CashoutResult>("bet:cashout", payload);
  }

  setClientSeed(seed: string): Promise<Ack<undefined>> {
    return this.emitWithAck<undefined>("client:seed", { seed });
  }

  private wire(): void {
    this.socket.on("connect", () => {
      this.handlers.onStatus("connected");
      this.clock.reset();
      this.publishClockHealth();
      this.sendPing();
      this.startPinging();
    });

    this.socket.io.on("reconnect_attempt", () => {
      this.handlers.onStatus("reconnecting");
    });

    this.socket.on("disconnect", () => {
      this.stopPinging();
      // The offset is meaningless across a disconnect: force a re-sync.
      this.clock.reset();
      this.publishClockHealth();
      this.handlers.onStatus("disconnected");
    });

    for (const event of SERVER_EVENT_NAMES) {
      // socket.io's typed `on` cannot narrow a loop variable. The payload is
      // validated by `parse` on the very next line, which is what matters.
      this.socket.on(
        event as never,
        ((raw: unknown) => {
          const payload = this.parse(event, raw);
          if (payload === null) return;
          this.handleEvent(event, payload);
        }) as never,
      );
    }
  }

  private handleEvent<K extends ServerEventName>(event: K, payload: ServerEventPayload<K>): void {
    switch (event) {
      case "server:time": {
        const sample = payload as ServerEventPayload<"server:time">;
        if (this.clock.acceptSample(sample)) this.publishClockHealth();
        return; // the clock is not store state
      }
      case "round:tick": {
        const tick = payload as ServerEventPayload<"round:tick">;
        if (this.clock.checkDrift(tick.serverTime)) this.sendPing();
        return;
      }
      case "state:sync":
        this.syncMultiplier(payload as ServerEventPayload<"state:sync">);
        break;
      case "round:betting":
        this.multiplier?.reset();
        break;
      case "round:start": {
        const start = payload as ServerEventPayload<"round:start">;
        if (start.growthK !== undefined) this.multiplier?.configure({ growthK: start.growthK });
        this.multiplier?.start(start.startedAt);
        break;
      }
      case "round:crash":
        this.multiplier?.crash((payload as ServerEventPayload<"round:crash">).crashAt);
        break;
      default:
        break;
    }
    this.handlers.onEvent(event, payload);
  }

  /** Rebuild the multiplier state from a full sync (reconnection mid-round). */
  private syncMultiplier(sync: ServerEventPayload<"state:sync">): void {
    const model = this.multiplier;
    if (!model) return;
    model.configure({ growthK: sync.config.growthK, maxMultiplier: sync.config.maxMultiplier });
    model.reset();
    if (sync.round.startedAt !== null) {
      model.start(sync.round.startedAt);
      if (sync.round.crashAt !== null) model.crash(sync.round.crashAt);
    }
  }

  private startPinging(): void {
    this.stopPinging();
    this.pingTimer = setInterval(() => {
      this.sendPing();
    }, PING_INTERVAL_MS);
  }

  private stopPinging(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private sendPing(): void {
    this.socket.emit("ping", this.clock.beginPing());
  }

  private publishClockHealth(): void {
    this.handlers.onClockHealth({ latencyMs: this.clock.rttMs, synced: this.clock.synced() });
  }

  private emitWithAck<T>(event: AckEvent, payload: object): Promise<Ack<T>> {
    return new Promise((resolve) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        resolve({ ok: false, error: { code: "INTERNAL", message: "ack timeout" } });
      }, ACK_TIMEOUT_MS);

      // The typed emit signature cannot be resolved from a union of event
      // names; the payloads are built by the typed methods above.
      const emitter = this.socket as unknown as LooseEmitter;
      emitter.emit(event, payload, (res: Ack<T>) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(res);
      });
    });
  }
}

/** Idempotency key, so a retried bet is never counted twice by the server. */
function newClientRef(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "ref-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}
